/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {Link} from '@ion/core/src/types/entities';
import Chip from '@ion/dls/src/components/chip/Chip';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {MaterialExitCloseIcon} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {IconSize} from '@ion/dls/src/types/types';

export interface DeviceGroupsChipProps {
  groups?: Link[];
  onDelete?: Function;
  isDelete?: boolean;
  isSwapDevice?: boolean;
}

const useStyles = makeStyles(() => ({
  chipContainer : {
    display   : 'flex',
    flexWrap  : 'wrap',
    maxHeight : '80px',
    overflowY : 'auto'
  },
  chipWrapper : {
    marginBottom : '10px',
    position     : 'relative'
  }
}));

/**
 * Defines the DeviceGroupsChip component. This component is used to
 * display the group names with tooltip when we click on more groups link
 *
 * ```typescript
 *
 *   import DeviceGroupsChip from './DeviceGroupsChip';
 *
 *   <DeviceGroupsChip  groups={groups} />
 *
 * ```
 */
const DeviceGroupsChip = ({
  groups,
  onDelete,
  isDelete = false,
  isSwapDevice = false
}: DeviceGroupsChipProps) => {
  const classes    = useStyles();
  const labelColor = dlsColor('grey2');

  return (
    <FormSection title={isSwapDevice
      ? translate('admin-devices:swap.selectedGroups')
      : translate('admin-devices:view.generalDeviceInfo.groups')}>
      <FormItem fullWidth>
        <div className={classes.chipContainer}>
          {groups?.map((group, index) => {
            return (
              <div key={`${group}-${index}`} className={classes.chipWrapper}>
                <Chip label={group?.title}
                  customDeleteIcon={isDelete ? <MaterialExitCloseIcon size={IconSize.small} /> : undefined}
                  onDelete={isDelete ? onDelete(group) : undefined}
                  showCustomDeleteIcon={isDelete}
                  labelColor={labelColor}
                  borderRadius='56px'
                  size='small'
                  maxWidth={false}
                  hoverDefault
                  noTextTransform />
              </div>
            );
          })}
          {(!isSwapDevice && !groups?.length) && <div>
            {translate('admin-devices:view.generalDeviceInfo.noGroup')}
          </div>}
        </div>
      </FormItem>
    </FormSection>
  );
};

export default React.memo(DeviceGroupsChip);
